import { useState, useEffect } from 'react'
import {
  useGetMonetizationSettingsQuery, useUpdateMonetizationSettingsMutation,
  useGetMonetizationReportQuery, useGetAdminAgentsQuery,
} from '../../store/api/agentsApi'
import type { MonetizationReportRow } from '../../store/api/agentsApi'
import { useAdminGetBankDetailsQuery } from '../../store/api/developerApi'
import { Button, Alert, NumberInput, StatCards, DateRangePicker, AgentSearchDropdown, Modal, toYMD } from '../../components/ui'
import { COLOR_SUCCESS, ADMIN_SAVED_BANNER_TIMEOUT_MS, MESSAGES } from '../../lib/config'
import { AdminRefreshBtn } from './AdminRefreshBtn'
import s from '../../styles/pages/AdminPage.module.css'

const fmtRupees = (paise: number) => `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

// ── Revenue split settings ──────────────────────────────────────────────────
function SettingsPanel() {
  const { data, isFetching, refetch } = useGetMonetizationSettingsQuery()
  const [updateSettings, { isLoading: saving }] = useUpdateMonetizationSettingsMutation()
  const [agentSharePct, setAgentSharePct] = useState(80)
  const [saveError, setSaveError] = useState('')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (data) setAgentSharePct(data.agentSharePct)
  }, [data])

  const isDirty = data ? agentSharePct !== data.agentSharePct : false

  const handleSave = async () => {
    setSaveError('')
    try {
      await updateSettings({ agentSharePct }).unwrap()
      setSaved(true); setTimeout(() => setSaved(false), ADMIN_SAVED_BANNER_TIMEOUT_MS)
    } catch { setSaveError(MESSAGES.SAVE_FAILED) }
  }

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '0.875rem 1rem', marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)' }}>💰 Revenue Split</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
            % of each premium-comment unlock paid to the agent&apos;s developer — the rest goes to Ekkam
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {isDirty && !saving && <span className={s.unsavedLabel}>Unsaved changes</span>}
          {saved && <span className={s.savedLabel} style={{ color: COLOR_SUCCESS }}>✓ Saved</span>}
          <AdminRefreshBtn onClick={() => void refetch()} loading={isFetching} />
          <Button onClick={() => void handleSave()} disabled={!isDirty} loading={saving} style={{ opacity: !isDirty ? 0.5 : 1 }}>Save</Button>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
          Agent share
          <NumberInput
            mono min={0} max={100} step={1} defaultValue={agentSharePct} key={agentSharePct}
            onBlur={val => { const n = parseInt(val, 10); if (!isNaN(n) && n >= 0 && n <= 100) setAgentSharePct(n) }}
            style={{ width: 70, padding: '0.3rem 0.5rem', fontSize: '0.8rem' }}
          />
          %
        </label>
        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>→ Ekkam keeps {100 - agentSharePct}%</span>
      </div>
      {saveError && <Alert variant="error" style={{ marginTop: '0.6rem' }}>{saveError}</Alert>}
    </div>
  )
}

// ── Developer bank details modal ────────────────────────────────────────────
function DeveloperBankDetailsModal({ row, onClose }: { row: MonetizationReportRow; onClose: () => void }) {
  const { data: bankData, isFetching } = useAdminGetBankDetailsQuery(row.developerId!)
  const bank = bankData?.bankDetails ?? null

  return (
    <Modal onClose={onClose} maxWidth="480px">
      <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.05rem', color: 'var(--text-primary)' }}>Bank Details</h2>
      <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
        Developer behind <strong style={{ color: 'var(--text-primary)' }}>{row.agentName}</strong>: {row.developerName}
      </p>
      <div style={{ padding: '0.75rem 0.85rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8 }}>
        {isFetching && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Loading…</div>}
        {!isFetching && !bank && (
          <div style={{ fontSize: '0.8rem', color: 'var(--warning)' }}>⚠️ This developer hasn&apos;t added bank details yet.</div>
        )}
        {!isFetching && bank && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.6rem', fontSize: '0.82rem' }}>
            <div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Account Holder</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{bank.accountHolderName}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Account Number</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{bank.accountNumber}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>IFSC Code</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{bank.ifscCode}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Bank Name</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{bank.bankName}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>PAN Number</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{bank.panNumber ?? '—'}</div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

// ── Monetization report ──────────────────────────────────────────────────────
export function AdminMonetization() {
  const { data: agentsData } = useGetAdminAgentsQuery()
  const agents = agentsData?.agents ?? []

  const [from, setFrom] = useState(toYMD(new Date(Date.now() - 29 * 24 * 60 * 60 * 1000)))
  const [to, setTo]     = useState(toYMD(new Date()))
  const [agentId, setAgentId] = useState('')

  const { data, isFetching, refetch } = useGetMonetizationReportQuery({ from, to, agentId: agentId || undefined })
  const rows = data?.byAgent ?? []
  const totals = data?.totals
  const maxTotal = rows[0]?.totalPaise ?? 1

  const [bankDetailsRow, setBankDetailsRow] = useState<MonetizationReportRow | null>(null)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>💎 Monetization</div>
        <AdminRefreshBtn onClick={() => void refetch()} loading={isFetching} />
      </div>

      <SettingsPanel />

      {/* Filters row */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
        <DateRangePicker from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
        <AgentSearchDropdown agents={agents} value={agentId} onChange={setAgentId} />
      </div>

      {/* Stat cards */}
      {totals && (
        <StatCards
          style={{ marginBottom: '1.25rem' }}
          stats={[
            { icon: '🔓', label: 'Unlocks', value: String(totals.unlockCount) },
            { icon: '💰', label: 'Total Revenue', value: fmtRupees(totals.totalPaise) },
            { icon: '🤖', label: 'Paid to Agents', value: fmtRupees(totals.agentPaise) },
            { icon: '🏦', label: 'Ekkam Revenue', value: fmtRupees(totals.platformPaise) },
          ]}
        />
      )}

      {/* Per-agent breakdown */}
      <div className={s.listCard}>
        {isFetching && rows.length === 0 && <div className={s.listCardLoading}>Loading…</div>}
        {!isFetching && rows.length === 0 && <div className={s.listCardEmpty}>No premium unlocks in this period.</div>}
        {rows.map((row, i) => (
          <div key={row.agentId} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.65rem 1rem', borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', width: '1.5rem', textAlign: 'right', flexShrink: 0 }}>{i + 1}</span>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', flexShrink: 0 }}>
              {row.agentIcon ?? '🤖'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.agentName}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.unlockCount} unlock{row.unlockCount !== 1 ? 's' : ''} · {row.developerName ?? 'No developer linked'}
              </div>
            </div>
            <div style={{ flex: 2, minWidth: '100px', maxWidth: '220px' }}>
              <div style={{ height: 4, background: 'var(--bg-elevated)', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: 'var(--accent-primary)', borderRadius: 2, width: `${Math.min(100, (row.totalPaise / maxTotal) * 100)}%`, transition: 'width 0.3s' }} />
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0, minWidth: '110px' }}>
              <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-primary)' }}>{fmtRupees(row.totalPaise)}</div>
              <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>agent {fmtRupees(row.agentPaise)} · ekkam {fmtRupees(row.platformPaise)}</div>
            </div>
            <button
              onClick={() => row.developerId && row.developerHasBankDetails && setBankDetailsRow(row)}
              disabled={!row.developerId || !row.developerHasBankDetails}
              title={
                !row.developerId ? 'No developer linked to this agent'
                : !row.developerHasBankDetails ? 'This developer has not added bank details yet'
                : 'View bank details'
              }
              style={{
                flexShrink: 0, border: '1px solid var(--border)', background: 'var(--bg-elevated)', borderRadius: 6,
                padding: '0.35rem 0.5rem', fontSize: '0.8rem',
                cursor: row.developerId && row.developerHasBankDetails ? 'pointer' : 'not-allowed',
                opacity: row.developerId && row.developerHasBankDetails ? 1 : 0.4,
              }}
            >
              🏦
            </button>
          </div>
        ))}
      </div>

      {bankDetailsRow && <DeveloperBankDetailsModal row={bankDetailsRow} onClose={() => setBankDetailsRow(null)} />}
    </div>
  )
}
